import React, { useState, useEffect, useContext, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { AppContext } from '../../application/provider';
import PlanillaService from '../../axios_services/planilla.service';
import UsuariosService from '../../axios_services/usuarios.service';
import Loader from '../Loader/Loader';

const EMPTY_FORM = {
    usuario_id: '',
    apellidos_nombre: '',
    total_produccion: 0,
    monto_regularizar: 0,
    bono_anual: 0,
    descuento_extra: 0,
    descuento_cancelados: 0,
    descuento_documento_observado: 0,
    retencion: 0,
    total_pagar: 0,
    importe_neto_pagar: 0,
    dni: '',
    n_cuenta: '',
    n_cuenta_interbancaria: '',
    entidad_financiera_id: '',
    fecha_pago: '',
};

const ModalRegistroCargoPago = ({ isOpen, toggle, onSaveSuccess, periodo }) => {
    const [state] = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);
    const [perfilF, setPerfilF] = useState('');
    const [usuarios, setUsuarios] = useState([]);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [reciboFile, setReciboFile] = useState(null);
    const fileRef = useRef(null);

    const perfiles = state.catalogos?.perfiles || [];
    // Perfiles pertenecientes a Negocios (macro_area_id = 2) o comerciales (3, 4, 27, 29)
    const filteredPerfiles = perfiles.filter(p => 
        String(p.macro_area_id) === '2' || [3, 4, 27, 29].includes(Number(p.perfil_id))
    );
    const perfilesList = filteredPerfiles.length > 0 ? filteredPerfiles : perfiles;

    const entidades_bancarias = state.catalogos?.tipos?.filter(t => Number(t.categoria_id) === 25) || [];

    useEffect(() => {
        if (isOpen) {
            setFormData(EMPTY_FORM);
            setReciboFile(null);
            if (fileRef.current) fileRef.current.value = '';
            
            const initialPerfil = perfilesList.length > 0 ? String(perfilesList[0].perfil_id) : '';
            setPerfilF(initialPerfil);
            loadUsuarios(initialPerfil);
        }
    }, [isOpen]);

    const loadUsuarios = async (selectedPerfilId) => {
        setIsLoading(true);
        try {
            const resp = await UsuariosService.getUsuariosPorPeriodo(0, periodo || '');
            const allUsers = resp.usuarios || resp.data || (Array.isArray(resp) ? resp : []);
            
            let filtered = allUsers.filter(u => String(u.estado) === '1');
            if (selectedPerfilId) {
                filtered = filtered.filter(u => String(u.perfil_id) === String(selectedPerfilId));
            }
            setUsuarios(filtered);
        } catch (e) {
            console.error("Error al cargar colaboradores:", e);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePerfilChange = (e) => {
        const pId = e.target.value;
        setPerfilF(pId);
        loadUsuarios(pId);
        setFormData(prev => ({
            ...prev,
            usuario_id: '',
            apellidos_nombre: '',
            dni: '',
            n_cuenta: '',
            n_cuenta_interbancaria: '',
            entidad_financiera_id: ''
        }));
    };

    const handleUsuarioSelect = (e) => {
        const userId = e.target.value;
        const user = usuarios.find(u => String(u.usuario_id) === String(userId));
        if (user) {
            const fullName = (user.nombre_completo || user.nombre_completo_usuario || `${user.apellidos || ''} ${user.nombre || ''}`).trim().toUpperCase();

            setFormData(prev => ({
                ...prev,
                usuario_id: user.usuario_id,
                apellidos_nombre: fullName,
                dni: user.dni || '',
                n_cuenta: user.n_cuenta_bancaria || user.cuenta || '',
                n_cuenta_interbancaria: user.n_cuenta_interbancaria || user.cci || '',
                entidad_financiera_id: user.entidad_financiera_id || ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                usuario_id: '',
                apellidos_nombre: '',
                dni: '',
                n_cuenta: '',
                n_cuenta_interbancaria: '',
                entidad_financiera_id: ''
            }));
        }
    };

    const calcularMontos = (data) => {
        const produccion = parseFloat(data.total_produccion) || 0;
        const regularizar = parseFloat(data.monto_regularizar) || 0;
        const bonoAnual = parseFloat(data.bono_anual) || 0;
        const extra = parseFloat(data.descuento_extra) || 0;
        const cancelado = parseFloat(data.descuento_cancelados) || 0;
        const observado = parseFloat(data.descuento_documento_observado) || 0;
        const retencion = parseFloat(data.retencion) || 0;

        const totalPagar = (produccion + regularizar + bonoAnual) - (extra + cancelado + observado);
        const importeNeto = totalPagar - retencion;

        return {
            total_pagar: parseFloat(totalPagar.toFixed(2)),
            importe_neto_pagar: parseFloat(importeNeto.toFixed(2))
        };
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            const calc = calcularMontos(updated);
            updated.total_pagar = calc.total_pagar;
            updated.importe_neto_pagar = calc.importe_neto_pagar;
            return updated;
        });
    };

    const handleSave = async () => {
        if (!formData.usuario_id) {
            alert('Por favor, selecciona un colaborador.');
            return;
        }

        setIsLoading(true);
        try {
            const payload = new FormData();
            payload.append('usuario_id', formData.usuario_id);
            payload.append('periodo_planilla', periodo);
            payload.append('total_produccion', formData.total_produccion || 0);
            payload.append('monto_regularizar', formData.monto_regularizar || 0);
            payload.append('bono_anual', formData.bono_anual || 0);
            payload.append('descuento_extra', formData.descuento_extra || 0);
            payload.append('descuento_cancelados', formData.descuento_cancelados || 0);
            payload.append('descuento_documento_observado', formData.descuento_documento_observado || 0);
            payload.append('retencion', formData.retencion || 0);
            payload.append('total_pagar', formData.total_pagar || 0);
            payload.append('usuario_creo_id', state.user?.usuario_id || 1);
            if (formData.fecha_pago) {
                payload.append('fecha_pago', formData.fecha_pago);
            }
            if (reciboFile) {
                payload.append('archivo_recibo_honorarios', reciboFile);
            }

            const res = await PlanillaService.guardarCargoPagoRegistroManual(payload);
            alert(res.message || 'Registro guardado exitosamente.');
            if (onSaveSuccess) {
                await onSaveSuccess();
            }
            toggle();
        } catch (error) {
            console.error('Error al registrar en Cargo Pago:', error);
            alert('Error al guardar: ' + (error.response?.data?.error || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" size="md" centered>
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>NUEVO REGISTRO - PLANILLA NEGOCIOS</h1>
                    <button className="close-btn" onClick={toggle}>X</button>
                </div>
            </ModalHeader>
            <div className="modal-body-footer">
                <ModalBody className="modal-body p-4">
                    {isLoading && <Loader />}
                    <Form className="px-4 mt-2">
                        
                        <div className="mb-4">
                            <FormGroup className="mb-3">
                                <Label>Seleccionar Perfil</Label>
                                <Input 
                                    type="select" 
                                    value={perfilF} 
                                    onChange={handlePerfilChange}
                                    className="form-control"
                                    style={{ backgroundColor: '#f0f7ff' }}
                                >
                                    <option value="">Seleccione un perfil...</option>
                                    {perfilesList.map(p => (
                                        <option key={p.perfil_id} value={p.perfil_id}>
                                            {p.descripcion.toUpperCase()}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>

                            <FormGroup className="mb-3">
                                <Label>Seleccionar Usuario</Label>
                                <Input 
                                    type="select" 
                                    value={formData.usuario_id}
                                    onChange={handleUsuarioSelect}
                                    className="form-control"
                                    disabled={isLoading}
                                    style={{ backgroundColor: '#f0f7ff' }}
                                >
                                    <option value="">{isLoading ? 'Cargando colaboradores...' : 'Seleccione un colaborador...'}</option>
                                    {usuarios.map(u => (
                                        <option key={u.usuario_id} value={u.usuario_id}>
                                            {(u.nombre_completo || u.nombre_completo_usuario || `${u.apellidos || ''} ${u.nombre || ''}`).trim().toUpperCase()}
                                        </option>
                                    ))}
                                </Input>
                                {isLoading && <div className="text-center mt-2"><small className="text-primary">Cargando lista de usuarios...</small></div>}
                            </FormGroup>
                            <hr />
                        </div>

                        <FormGroup className="mb-3">
                            <Label for="apellidos_nombre">Apellidos y Nombres</Label>
                            <Input 
                                type="text" 
                                name="apellidos_nombre" 
                                id="apellidos_nombre" 
                                value={formData.apellidos_nombre} 
                                readOnly
                                className="form-control"
                                placeholder="Ingresa los apellidos y nombres completos"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="total_produccion">Pago por Producción Mes</Label>
                            <Input 
                                type="number" 
                                name="total_produccion" 
                                id="total_produccion" 
                                value={formData.total_produccion} 
                                onChange={handleChange} 
                                className="form-control"
                                placeholder="Ingresa el pago por producción"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="monto_regularizar">Monto a Regularizar Meses Anteriores</Label>
                            <Input 
                                type="number" 
                                name="monto_regularizar" 
                                id="monto_regularizar" 
                                value={formData.monto_regularizar} 
                                onChange={handleChange} 
                                className="form-control" 
                                placeholder="0.00" 
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="bono_anual">Bono Anual</Label>
                            <Input 
                                type="number" 
                                name="bono_anual" 
                                id="bono_anual" 
                                value={formData.bono_anual} 
                                onChange={handleChange} 
                                className="form-control" 
                                placeholder="0.00" 
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="descuento_extra">Desc. Extra</Label>
                            <Input 
                                type="number" 
                                name="descuento_extra" 
                                id="descuento_extra" 
                                value={formData.descuento_extra} 
                                onChange={handleChange} 
                                className="form-control" 
                                placeholder="0.00" 
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="descuento_cancelados">Desc. Cancelado</Label>
                            <Input 
                                type="number" 
                                name="descuento_cancelados" 
                                id="descuento_cancelados" 
                                value={formData.descuento_cancelados} 
                                onChange={handleChange} 
                                className="form-control" 
                                placeholder="0.00" 
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="descuento_documento_observado">Desc. por Documento Observado</Label>
                            <Input 
                                type="number" 
                                name="descuento_documento_observado" 
                                id="descuento_documento_observado" 
                                value={formData.descuento_documento_observado} 
                                onChange={handleChange} 
                                className="form-control" 
                                placeholder="0.00" 
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="retencion">Retención</Label>
                            <Input 
                                type="number" 
                                name="retencion" 
                                id="retencion" 
                                value={formData.retencion} 
                                onChange={handleChange} 
                                className="form-control" 
                                placeholder="0.00" 
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="importe_neto_pagar" style={{ fontWeight: 'bold' }}>Importe Neto a Pagar</Label>
                            <Input 
                                type="number" 
                                name="importe_neto_pagar" 
                                id="importe_neto_pagar" 
                                value={formData.importe_neto_pagar} 
                                disabled 
                                className="form-control" 
                                style={{ fontWeight: 'bold' }} 
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="dni">DNI</Label>
                            <Input 
                                type="text" 
                                name="dni" 
                                id="dni" 
                                value={formData.dni} 
                                readOnly
                                className="form-control"
                                placeholder="Ingresa el número de DNI"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="entidad_financiera_id">Entidad Bancaria</Label>
                            <Input 
                                type="select" 
                                name="entidad_financiera_id" 
                                id="entidad_financiera_id" 
                                value={formData.entidad_financiera_id} 
                                disabled
                                className="form-control"
                            >
                                <option value="">Seleccione un banco...</option>
                                {entidades_bancarias.map(b => (
                                    <option key={b.tipo_id} value={b.tipo_id}>
                                        {b.descripcion.toUpperCase()}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="n_cuenta">N° Cuenta</Label>
                            <Input 
                                type="text" 
                                name="n_cuenta" 
                                id="n_cuenta" 
                                value={formData.n_cuenta} 
                                readOnly
                                className="form-control"
                                placeholder="Ingresa el número de cuenta bancaria"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="n_cuenta_interbancaria">N° Cuenta Interbancaria (CCI)</Label>
                            <Input 
                                type="text" 
                                name="n_cuenta_interbancaria" 
                                id="n_cuenta_interbancaria" 
                                value={formData.n_cuenta_interbancaria} 
                                readOnly
                                className="form-control"
                                placeholder="Ingresa el código de cuenta interbancaria"
                            />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <Label>Subir Recibo por Honorarios (PDF):</Label>
                            <div className="cap-upload-zone">
                                <span className="cap-upload-btn" onClick={() => fileRef.current?.click()}>
                                    {reciboFile ? reciboFile.name : 'Seleccionar archivo (PDF)'}
                                </span>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    style={{ display: 'none' }}
                                    onChange={(e) => setReciboFile(e.target.files[0] || null)}
                                />
                            </div>
                            {reciboFile && (
                                <small>
                                    <span 
                                        style={{ cursor: 'pointer', color: '#f87171', fontSize: '10px' }} 
                                        onClick={() => { setReciboFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                                    >
                                        ✕ Quitar selección
                                    </span>
                                </small>
                            )}
                        </FormGroup>

                        <div className="flex justify-end mt-4 pb-3">
                            <Button
                                className="btn-primary px-5 py-2"
                                onClick={handleSave}
                                disabled={isLoading}
                                style={{ borderRadius: '8px' }}
                            >
                                GUARDAR
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalRegistroCargoPago;
